import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PAGE_SIZE } from "../../../services/common/const";
import {
  DeleteVaccineTemplate,
  GetVaccineTemplateList,
  UpdateDefaultNotificationTemplate,
} from "../../../services/VaccineTemplateService";
import { HandleUpdateAllMemberVaccineModule } from "../../../services/MemberVaccineModuleService";
import type { RootState } from "../../index";

export type PopupActionType = "APPLY" | "REMOVE" | "";

export interface VaccineTemplateContext {
  consumerId: number | string;
  organizationId: number | string;
  branchId: number | string;
  specialityId?: number | string;
  departmentId?: number | string;
  dataManagedBy?: number | string;
}

export interface VaccineTemplateItem {
  vaccineTemplateId: number;
  templateName: string;
  isDefault?: boolean;
}

export interface VaccineTemplateResultData {
  vaccineTemplateColl: VaccineTemplateItem[];
  tabTypeColl: unknown[];
  totalCount: number;
}

export interface VaccineTemplateListParam {
  vaccineTemplateId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
}

interface CustomPopupState {
  open: boolean;
  name: string;
  code: string;
  action: PopupActionType;
}

export interface VaccineTemplateState {
  param: VaccineTemplateListParam;
  searchValue: string;
  currentPage: number;
  pageCount: number;
  totalCount: number;
  pageStartIndex: number;
  pageEndIndex: number;
  resultData: VaccineTemplateResultData;
  isLoading: boolean;
  showSkeleton: boolean;
  openDeleteDialog: boolean;
  selectedTemplateId: number;
  selectedTemplateName: string;
  selectedTemplateDefault: boolean;
  applyProcessingTemplateIds: number[];
  customPopup: CustomPopupState;
  errorMessage: string;
}

const initialParams: VaccineTemplateListParam = {
  vaccineTemplateId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
};

const initResultData: VaccineTemplateResultData = {
  vaccineTemplateColl: [],
  tabTypeColl: [],
  totalCount: 0,
};

const initialState: VaccineTemplateState = {
  param: initialParams,
  searchValue: "",
  currentPage: 1,
  pageCount: 0,
  totalCount: 0,
  pageStartIndex: 1,
  pageEndIndex: 1,
  resultData: initResultData,
  isLoading: true,
  showSkeleton: true,
  openDeleteDialog: false,
  selectedTemplateId: 0,
  selectedTemplateName: "",
  selectedTemplateDefault: false,
  applyProcessingTemplateIds: [],
  customPopup: {
    open: false,
    name: "",
    code: "",
    action: "",
  },
  errorMessage: "",
};

const toStringSafe = (value: number | string | undefined) =>
  value === undefined || value === null ? "" : value.toString();

const parseListResponse = (
  data: any,
): {
  resultData: VaccineTemplateResultData;
  pageCount: number;
  totalCount: number;
  pageStartIndex: number;
  pageEndIndex: number;
} => {
  const responseData = data?.data?.data || {};
  const vaccineTemplateColl: VaccineTemplateItem[] =
    responseData?.vaccineTemplateColl || [];
  const totalCount: number = responseData?.totalCount || 0;
  const pageSize = responseData?.pageSize || PAGE_SIZE;
  const pageIndex = responseData?.pageIndex || 1;
  const pageCount = Math.ceil(totalCount / pageSize) || 0;
  const pageStartIndex = totalCount === 0 ? 0 : (pageIndex - 1) * pageSize + 1;
  const pageEndIndex =
    totalCount === 0
      ? 0
      : (pageIndex - 1) * pageSize + vaccineTemplateColl.length;

  return {
    resultData: {
      vaccineTemplateColl,
      tabTypeColl: responseData?.tabTypeColl || [],
      totalCount,
    },
    pageCount,
    totalCount,
    pageStartIndex,
    pageEndIndex,
  };
};

export const fetchVaccineTemplateList = createAsyncThunk<
  {
    resultData: VaccineTemplateResultData;
    pageCount: number;
    totalCount: number;
    pageStartIndex: number;
    pageEndIndex: number;
  },
  VaccineTemplateContext,
  { state: RootState; rejectValue: string }
>(
  "vaccineTemplate/fetchList",
  async (context, { getState, rejectWithValue }) => {
    try {
      const { param } = getState().vaccineTemplate;
      const params = new URLSearchParams();
      params.append("consumerId", toStringSafe(context.consumerId));
      params.append("orgId", toStringSafe(context.organizationId));
      params.append("branchId", toStringSafe(context.branchId));
      params.append("pageIndex", param.pageIndex.toString());
      params.append("pageSize", param.pageSize.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      const response = await GetVaccineTemplateList(params);
      return parseListResponse(response);
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "Failed to fetch vaccine templates",
      );
    }
  },
);

export const updateDefaultVaccineTemplate = createAsyncThunk<
  { message: string },
  VaccineTemplateContext,
  { state: RootState; rejectValue: string }
>(
  "vaccineTemplate/updateDefault",
  async (context, { getState, rejectWithValue }) => {
    try {
      const { selectedTemplateId } = getState().vaccineTemplate;
      const payload = {
        vaccineTemplateId: selectedTemplateId.toString(),
        consumerId: toStringSafe(context.consumerId),
        orgId: toStringSafe(context.organizationId),
        branchId: toStringSafe(context.branchId),
      };
      const result: any = await UpdateDefaultNotificationTemplate(payload);
      return {
        message: result?.message || "Default template updated successfully.",
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "Failed to update default template",
      );
    }
  },
);

export const deleteVaccineTemplate = createAsyncThunk<
  { message: string },
  VaccineTemplateContext,
  { state: RootState; rejectValue: string }
>("vaccineTemplate/delete", async (context, { getState, rejectWithValue }) => {
  try {
    const { selectedTemplateId } = getState().vaccineTemplate;
    const payload = {
      vaccineTemplateIds: selectedTemplateId.toString(),
      consumerId: toStringSafe(context.consumerId),
      orgId: toStringSafe(context.organizationId),
      branchId: toStringSafe(context.branchId),
      isDeleted: true,
      dataManagedBy: context.dataManagedBy,
    };
    const result: any = await DeleteVaccineTemplate(payload);
    return {
      message: result?.message || "Template deleted successfully.",
    };
  } catch (error: any) {
    return rejectWithValue(error?.message || "Failed to delete template");
  }
});

export const updateAllMemberVaccineModule = createAsyncThunk<
  { success: boolean; message: string },
  VaccineTemplateContext & {
    actionType: Exclude<PopupActionType, "">;
    vaccineName?: string;
  },
  { state: RootState; rejectValue: string }
>(
  "vaccineTemplate/updateAllMember",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const { selectedTemplateId, selectedTemplateName } =
        getState().vaccineTemplate;
      const response: any = await HandleUpdateAllMemberVaccineModule({
        consumerId: payload.consumerId,
        orgId: payload.organizationId,
        branchId: payload.branchId,
        specialityId: payload.specialityId,
        departmentId: payload.departmentId,
        isActive: payload.actionType === "APPLY",
        vaccineTemplateId: selectedTemplateId,
        templateName: selectedTemplateName,
        name: payload.vaccineName || "",
        dataManagedBy: payload.dataManagedBy,
      });
      return {
        success: Boolean(response?.data),
        message: response?.message || "Vaccine module operation completed.",
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "Failed to update all member vaccine modules",
      );
    }
  },
);

const vaccineTemplateSlice = createSlice({
  name: "vaccineTemplate",
  initialState,
  reducers: {
    setCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload;
      state.param.pageIndex = action.payload;
    },
    setSearchValue(state, action: PayloadAction<string>) {
      state.searchValue = action.payload;
    },
    applySearch(state, action: PayloadAction<string>) {
      state.searchValue = action.payload;
      state.currentPage = 1;
      state.param.pageIndex = 1;
      state.param.searchValue = action.payload;
    },
    setOpenDeleteDialog(state, action: PayloadAction<boolean>) {
      state.openDeleteDialog = action.payload;
    },
    setTemplateContext(
      state,
      action: PayloadAction<{
        templateId: number;
        templateName: string;
        isDefault: boolean;
      }>,
    ) {
      state.selectedTemplateId = action.payload.templateId;
      state.selectedTemplateName = action.payload.templateName;
      state.selectedTemplateDefault = action.payload.isDefault;
    },
    openCustomPopup(
      state,
      action: PayloadAction<{
        name: string;
        code: string;
        action: PopupActionType;
      }>,
    ) {
      state.customPopup.open = true;
      state.customPopup.name = action.payload.name;
      state.customPopup.code = action.payload.code;
      state.customPopup.action = action.payload.action;
    },
    closeCustomPopup(state) {
      state.customPopup.open = false;
    },
    markApplyProcessing(state, action: PayloadAction<number>) {
      const templateId = action.payload;
      if (templateId > 0 && !state.applyProcessingTemplateIds.includes(templateId)) {
        state.applyProcessingTemplateIds.push(templateId);
      }
    },
    unmarkApplyProcessing(state, action: PayloadAction<number>) {
      const templateId = action.payload;
      state.applyProcessingTemplateIds = state.applyProcessingTemplateIds.filter(
        (id) => id !== templateId,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVaccineTemplateList.pending, (state) => {
        state.isLoading = true;
        state.showSkeleton = true;
        state.errorMessage = "";
      })
      .addCase(fetchVaccineTemplateList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.showSkeleton = false;
        state.resultData = action.payload.resultData;
        state.pageCount = action.payload.pageCount;
        state.totalCount = action.payload.totalCount;
        state.pageStartIndex = action.payload.pageStartIndex;
        state.pageEndIndex = action.payload.pageEndIndex;
      })
      .addCase(fetchVaccineTemplateList.rejected, (state, action) => {
        state.isLoading = false;
        state.showSkeleton = false;
        state.errorMessage =
          action.payload || "Failed to fetch vaccine templates";
      })
      .addCase(updateDefaultVaccineTemplate.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateDefaultVaccineTemplate.fulfilled, (state) => {
        state.isLoading = false;
        state.openDeleteDialog = false;
      })
      .addCase(updateDefaultVaccineTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.errorMessage =
          action.payload || "Failed to update default template";
      })
      .addCase(deleteVaccineTemplate.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteVaccineTemplate.fulfilled, (state) => {
        state.isLoading = false;
        state.openDeleteDialog = false;
      })
      .addCase(deleteVaccineTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.errorMessage = action.payload || "Failed to delete template";
      })
      .addCase(updateAllMemberVaccineModule.pending, (state) => {
        state.isLoading = true;
        if (state.selectedTemplateId > 0) {
          const exists = state.applyProcessingTemplateIds.includes(
            state.selectedTemplateId,
          );
          if (!exists) {
            state.applyProcessingTemplateIds.push(state.selectedTemplateId);
          }
        }
      })
      .addCase(updateAllMemberVaccineModule.fulfilled, (state) => {
        state.isLoading = false;
        state.applyProcessingTemplateIds =
          state.applyProcessingTemplateIds.filter(
            (templateId) => templateId !== state.selectedTemplateId,
          );
      })
      .addCase(updateAllMemberVaccineModule.rejected, (state, action) => {
        state.isLoading = false;
        state.applyProcessingTemplateIds =
          state.applyProcessingTemplateIds.filter(
            (templateId) => templateId !== state.selectedTemplateId,
          );
        state.errorMessage = action.payload || "Failed to update all members";
      });
  },
});

export const {
  setCurrentPage,
  setSearchValue,
  applySearch,
  setOpenDeleteDialog,
  setTemplateContext,
  openCustomPopup,
  closeCustomPopup,
  markApplyProcessing,
  unmarkApplyProcessing,
} = vaccineTemplateSlice.actions;

export const selectVaccineTemplateState = (state: RootState) =>
  state.vaccineTemplate;

export default vaccineTemplateSlice.reducer;
